import React from 'react';
import sharedMessages from '@atlassian/generative-ai-modal/utils/messages';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
import { createEditorPluginAIConfigItemMarkdown } from '../../../config-items/config-items';
import { sliceOrNodeToMarkdown } from '../../../config-items/slice-or-node-to-markdown';
import { generate, generateConvoAI } from '../../../provider/prompt-requests/generate';
import { createInsertAtCurrentPosition } from '../../config-item-actions/markdown';
import { messages } from './messages';
import { default as Icon } from './Icon';
import { getBackendModelExperiment } from '../../utils';
const SELECTION_TYPE = 'empty';
export const generateContentConfigItem = createEditorPluginAIConfigItemMarkdown({
  title: ({
    formatMessage
  }) => formatMessage(sharedMessages.aiButtonTitle),
  description: ({
    formatMessage
  }) => formatMessage(sharedMessages.aiUniversalLongCallToAction),
  promptHint: ({
    formatMessage
  }) => formatMessage(messages.promptHint),
  selectionType: SELECTION_TYPE,
  getInitialContext: ({
    editorView,
    intl,
    updateIdMap
  }) => {
    const {
      markdown,
      contentStatistics
    } = sliceOrNodeToMarkdown({
      node: editorView.state.doc,
      intl,
      editorView,
      convertTo: 'markdown-plus',
      updateIdMap,
      selectionType: SELECTION_TYPE
    });
    return {
      document: markdown,
      userLocale: intl.locale,
      intentSchemaId: 'custom_free_generate_intent_schema.json',
      contentStatistics
    };
  },
  icon: ({
    formatMessage
  }) => /*#__PURE__*/React.createElement(Icon, {
    alt: formatMessage(sharedMessages.aiButtonTitle)
  }),
  // No prompt label is required -- as this is intended to be set via the
  // the baseGenerate
  triggerPromptRequest({
    initialContext,
    promptInput,
    latestPromptResponse,
    editorSchema,
    backendModel
  }) {
    // This config item is overloaded to support both the interrogation prompt and the free generate prompt
    // we distinguish which one we're in by checking if we have a latestPromptResponse
    let contextList;
    if (latestPromptResponse) {
      // If we have a latestPromptResponse, we don't want to add the document to the contextList
      // as it's an interrogation prompt
      contextList = [{
        type: 'ADF_MARKDOWN_V1',
        entity: latestPromptResponse,
        relationship: 'PREVIOUS_RESPONSE'
      }];
    } else {
      // We only add the full content based on a feature flag
      if (getBooleanFF('platform.editor.ai-send-full-content_r1qpp')) {
        contextList = !initialContext.document ? [] : [{
          type: 'ADF_MARKDOWN_V1',
          entity: initialContext.document,
          relationship: 'FULL_CONTENT'
        }];
      } else {
        // When the feature flag is off, we send this as an empty string as the backend expects this
        // to be present
        // see https://atlassian.slack.com/archives/C055QF79V7W/p1692262267702639 for context
        contextList = [{
          type: 'text',
          entity: '',
          relationship: undefined
        }];
      }
    }

    // Use Convo AI api for free generate if flag is on and it's not interrogate
    // Have kept this separate to interrogate in case they need to be toggled separately
    // for whatever reason (eg. generate works well but interrogate does not)
    if (getBooleanFF('platform.editor.ai-assistance-service-free-generate') && !latestPromptResponse) {
      return generateConvoAI({
        customPrompt: promptInput,
        userLocale: initialContext.userLocale,
        intentSchemaId: 'custom_free_generate_intent_schema.json',
        editorSchema,
        fullDocument: initialContext.document
      });
    }

    // Use Convo AI api for interrogate if flag is on
    if (getBooleanFF('platform.editor.ai-interrogate-with-convo-ai') && latestPromptResponse) {
      return generateConvoAI({
        customPrompt: promptInput,
        userLocale: initialContext.userLocale,
        intentSchemaId: 'custom_with_interrogation_intent_schema.json',
        editorSchema,
        latestPromptResponse,
        fullDocument: initialContext.document
      });
    }

    // Else use xp-gen-ai api
    return generate({
      customPrompt: promptInput,
      userLocale: initialContext.userLocale,
      intentSchemaId: latestPromptResponse ? 'custom_with_interrogation_intent_schema.json' : 'custom_free_generate_intent_schema.json',
      editorSchema,
      contextList,
      backendModel
    });
  },
  isVisible: () => true,
  actions: [createInsertAtCurrentPosition({
    appearance: 'primary'
  })],
  quickInsertPriority: Number.NEGATIVE_INFINITY,
  getBackendModel: isInterrogate => {
    // If we're not interrogating, we use xp-gen-ai
    if (!isInterrogate) {
      return 'xp-gen-ai';
    }
    return getBackendModelExperiment();
  }
});